import React, { useState, useEffect, useMemo, useCallback } from"react";
import { useTranslation } from"react-i18next";
import { useParams, useNavigate } from"react-router-dom";
import {
 FileText,
 DollarSign,
 User,
 Car,
 CheckCircle,
 Clock,
 XCircle,
 ArrowLeft,
 Download,
 Printer,
 CreditCard,
 Plus,
} from"lucide-react";
import {
 Card,
 CardHeader,
 CardTitle,
 CardContent,
 ModernButton,
 Badge,
 Tabs,
 TabsList,
 TabsTrigger,
 TabsContent,
 Table,
 TableHeader,
 TableBody,
 TableRow,
 TableHead,
 TableCell,
 Toast,
 ToastType,
 Select,
 SelectContent,
 SelectItem,
 SelectTrigger,
 SelectValue,
 Label,
 DetailPageSkeleton,
} from"../../components/ui";
import { apiService } from"../../services/api";
import { Invoice, Payment, Commission, CommissionRule, Sale } from"../../types";
import { formatCurrency, formatDateToBR } from"../../utils/formatters";
import {
 resolveCommissionDisplay,
 commissionRuleHasFixedPortion,
 commissionRuleFixedAmount,
 sumCommissionBonuses,
} from"../../utils/commissionCalculation";
import { commissionRuleAppliesToManualSaleAdd } from"../../utils/commissionRuleManualAdd";
import { CommissionActiveRulesTable } from"../../components/financial";

export const InvoiceDetailsPage: React.FC = () => {
 const { t } = useTranslation();
 const { id } = useParams<{ id: string }>();
 const navigate = useNavigate();
 const [invoice, setInvoice] = useState<Invoice | null>(null);
 const [isLoading, setIsLoading] = useState(true);
 const [toast, setToast] = useState<{
 message: string;
 type: ToastType;
 } | null>(null);
 const [addCommissionDialogOpen, setAddCommissionDialogOpen] = useState(false);
 const [availableRules, setAvailableRules] = useState<CommissionRule[]>([]);
 const [selectedRuleId, setSelectedRuleId] = useState<string>("");
 const [isCreatingCommission, setIsCreatingCommission] = useState(false);
 const [activeCommissionRulesCatalog, setActiveCommissionRulesCatalog] = useState<CommissionRule[]>([]);
 const [applyStoreRulesToAllSales, setApplyStoreRulesToAllSales] = useState(true);

 const loadCommissionRulesCatalog = useCallback(async () => {
 try {
 const [rules, me] = await Promise.all([
 apiService.getCommissionRules(),
 apiService.getDealershipMe().catch(() => null),
 ]);
 setActiveCommissionRulesCatalog((rules || []).filter((r: any) => r.isActive));
 if (me) {
 setApplyStoreRulesToAllSales(me.commissionApplyStoreRulesToAllSales !== false);
 }
 } catch {
 setActiveCommissionRulesCatalog([]);
 }
 }, []);

 const invoiceSaleCommissionEvalContext = useMemo(() => {
 const s = invoice?.sale as Sale | undefined;
 if (!s) return null;
 const vehicleType =
 (s.vehicle as { condition?: string } | undefined)?.condition ==="new"?"new":"used";
 const paymentFormIds = Array.from(
 new Set(
 ((s.proposal as { payments?: Array<{ paymentFormId?: string }> } | undefined)?.payments || [])
 .map((p) => p.paymentFormId)
 .filter(Boolean)
 )
 ) as string[];
 return {
 vehicleType,
 paymentFormIds,
 saleSalespersonId: s.salespersonId,
 applyStoreRulesToAllSales,
 };
 }, [invoice?.sale, applyStoreRulesToAllSales]);

 const sortedActiveCommissionRules = useMemo(() => {
 return [...activeCommissionRulesCatalog].sort((a, b) => {
 if (Boolean(a.isMandatory) !== Boolean(b.isMandatory)) {
 return a.isMandatory ? -1 : 1;
 }
 return a.name.localeCompare(b.name);
 });
 }, [activeCommissionRulesCatalog]);

 const visibleInvoiceCommissions = useMemo(
 () =>
 (invoice?.sale?.commissions ?? []).filter((commission) => {
 const ruleMeta =
 commission.rule ??
 sortedActiveCommissionRules.find((rule) => rule.id === commission.ruleId);
 return Boolean(ruleMeta?.isActive);
 }),
 [invoice?.sale?.commissions, sortedActiveCommissionRules]
 );

 useEffect(() => {
 if (id) {
 fetchInvoice();
 }
 }, [id]);

 useEffect(() => {
 if (addCommissionDialogOpen && invoice?.sale) {
 fetchAvailableCommissionRules();
 }
 }, [addCommissionDialogOpen, invoice?.sale]);

 const fetchInvoice = async () => {
 try {
 setIsLoading(true);
 const data = await apiService.getInvoiceById(id!);
 setInvoice(data);
 void loadCommissionRulesCatalog();
 } catch (err: any) {
 console.error("Erro ao carregar fatura:", err);
 setToast({
 message: err.message ||"Erro ao carregar fatura",
 type:"error",
 });
 } finally {
 setIsLoading(false);
 }
 };

 const fetchAvailableCommissionRules = async () => {
 if (!invoice?.sale) return;
 try {
 const allRules = await apiService.getCommissionRules();
 const sale = invoice.sale;
 const vehicleType =
 (sale.vehicle as any)?.condition ==="new"?"new":"used";
 const paymentFormIds = Array.from(
 new Set(
 ((sale.proposal as any)?.payments || [])
 .map((p: { paymentFormId?: string }) => p.paymentFormId)
 .filter(Boolean)
 )
 ) as string[];

 const activeOnly = (allRules || []).filter((r: CommissionRule) => r.isActive);

 const applicableRules = activeOnly.filter((rule: CommissionRule) => {
 if (
 !commissionRuleAppliesToManualSaleAdd(rule, {
 vehicleType,
 paymentFormIds,
 saleSalespersonId: sale.salespersonId,
 applyStoreRulesToAllSales,
 })
 ) {
 return false;
 }
 const existingCommission = sale.commissions?.find(
 (c: Commission) => c.ruleId === rule.id
 );
 return !existingCommission;
 });

 setAvailableRules(applicableRules);
 } catch (err: any) {
 console.error("Erro ao carregar regras de comissão:", err);
 setToast({
 message: err.message ||"Erro ao carregar regras de comissão",
 type:"error",
 });
 }
 };

 const handleAddCommission = async () => {
 if (!selectedRuleId || !invoice?.sale) return;

 try {
 setIsCreatingCommission(true);
 await apiService.createCommission({
 saleId: invoice.sale.id,
 ruleId: selectedRuleId,
 });

 setToast({
 message:"Comissão adicionada com sucesso",
 type:"success",
 });

 setAddCommissionDialogOpen(false);
 setSelectedRuleId("");

 // Refresh invoice data
 await fetchInvoice();
 } catch (err: any) {
 console.error("Erro ao adicionar comissão:", err);
 setToast({
 message: err.message ||"Erro ao adicionar comissão",
 type:"error",
 });
 } finally {
 setIsCreatingCommission(false);
 }
 };

 const getStatusIcon = (status: Invoice["status"]) => {
 switch (status) {
 case"paid":
 return <CheckCircle className="h-5 w-5 text-green-600"/>;
 case"issued":
 return <Clock className="h-5 w-5 text-blue-600"/>;
 case"overdue":
 return <XCircle className="h-5 w-5 text-red-600"/>;
 case"draft":
 return <FileText className="h-5 w-5 text-muted-foreground"/>;
 case"cancelled":
 return <XCircle className="h-5 w-5 text-muted-foreground"/>;
 default:
 return <FileText className="h-5 w-5 text-muted-foreground"/>;
 }
 };

 const getStatusLabel = (status: Invoice["status"]) => {
 const labels: Record<Invoice["status"], string> = {
 draft:"Rascunho",
 issued:"Emitida",
 paid:"Paga",
 overdue:"Vencida",
 cancelled:"Cancelada",
 };
 return labels[status] || status;
 };

 const getStatusColor = (status: Invoice["status"]) => {
 switch (status) {
 case"paid":
 return"bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
 case"issued":
 return"bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200";
 case"overdue":
 return"bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
 case"cancelled":
 return"bg-surface text-foreground dark:text-muted-foreground";
 default:
 return"bg-surface text-foreground dark:text-muted-foreground";
 }
 };

 const handlePrint = () => {
 window.print();
 };

 const handleExport = () => {
 // TODO: Implementar exportação para PDF
 setToast({
 message:"Funcionalidade de exportação em desenvolvimento",
 type:"info",
 });
 };

 if (isLoading) {
 return (
 <div className="space-y-6 max-w-6xl mx-auto">
 <DetailPageSkeleton />
 </div>
 );
 }

 if (!invoice) {
 return (
 <div className="space-y-6">
 <ModernButton
 variant="outline"
 onClick={() => navigate("/financial/invoices")}
 >
 <ArrowLeft className="h-4 w-4 mr-2"/>
 Voltar
 </ModernButton>
 <Card>
 <CardContent className="py-12 text-center">
 <FileText className="h-16 w-16 text-muted-foreground mx-auto mb-4"/>
 <h3 className="text-lg font-medium text-foreground mb-2">
 Fatura não encontrada
 </h3>
 <p className="text-muted-foreground">
 A fatura solicitada não foi encontrada ou você não tem permissão
 para visualizá-la.
 </p>
 </CardContent>
 </Card>
 </div>
 );
 }

 const totalAmount = parseFloat(invoice.totalAmount.toString());
 const paidAmount = parseFloat(invoice.paidAmount.toString());
 const remaining = totalAmount - paidAmount;
 const paymentPercentage =
 totalAmount > 0 ? (paidAmount / totalAmount) * 100 : 0;

 return (
 <div className="space-y-6">
 {/* Header */}
 <div className="flex items-center justify-between">
 <div className="flex items-center gap-4">
 <ModernButton
 variant="outline"
 onClick={() => navigate("/financial/invoices")}
 >
 <ArrowLeft className="h-4 w-4 mr-2"/>
 Voltar
 </ModernButton>
 <div>
 <h1 className="text-2xl font-bold text-foreground font-display">
 Fatura {invoice.invoiceNumber}
 </h1>
 <p className="text-sm text-muted-foreground mt-1">
 Detalhes da fatura
 </p>
 </div>
 </div>
 <div className="flex items-center gap-2">
 <ModernButton variant="outline"onClick={handleExport}>
 <Download className="h-4 w-4 mr-2"/>
 Exportar
 </ModernButton>
 <ModernButton variant="outline"onClick={handlePrint}>
 <Printer className="h-4 w-4 mr-2"/>
 Imprimir
 </ModernButton>
 </div>
 </div>

 {/* Status Card */}
 <Card>
 <CardContent className="pt-6">
 <div className="flex items-center justify-between">
 <div className="flex items-center gap-4">
 {getStatusIcon(invoice.status)}
 <div>
 <div className="flex items-center gap-2">
 <Badge className={getStatusColor(invoice.status)}>
 {getStatusLabel(invoice.status)}
 </Badge>
 </div>
 <p className="text-sm text-muted-foreground mt-1">
 Emitida em {formatDateToBR(invoice.issueDate)}
 </p>
 </div>
 </div>
 <div className="text-right">
 <div className="text-2xl font-bold text-foreground font-display">
 {formatCurrency(totalAmount)}
 </div>
 {remaining > 0 && (
 <div className="text-sm text-amber-600 dark:text-amber-400 mt-1">
 Restante: {formatCurrency(remaining)}
 </div>
 )}
 </div>
 </div>

 {/* Progress Bar */}
 {totalAmount > 0 && (
 <div className="mt-4">
 <div className="flex items-center justify-between text-sm mb-2">
 <span className="text-muted-foreground">
 Valor pago: {formatCurrency(paidAmount)}
 </span>
 <span className="text-muted-foreground">
 {paymentPercentage.toFixed(1)}%
 </span>
 </div>
 <div className="w-full bg-surface rounded-full h-2">
 <div
 className="bg-green-600 h-2 rounded-full transition-all"
 style={{ width: `${paymentPercentage}%` }}
 />
 </div>
 </div>
 )}
 </CardContent>
 </Card>

 {/* Tabs */}
 <Tabs defaultValue="overview"className="space-y-4">
 <TabsList>
 <TabsTrigger value="overview">Visão Geral</TabsTrigger>
 <TabsTrigger value="payments">Pagamentos</TabsTrigger>
 <TabsTrigger value="commissions">Comissões</TabsTrigger>
 <TabsTrigger value="sale">Venda Relacionada</TabsTrigger>
 </TabsList>

 {/* Overview Tab */}
 <TabsContent value="overview"className="space-y-4">
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 {/* Informações da Fatura */}
 <Card>
 <CardHeader>
 <CardTitle className="flex items-center gap-2">
 <FileText className="h-5 w-5"/>
 Informações da Fatura
 </CardTitle>
 </CardHeader>
 <CardContent className="space-y-3">
 <div>
 <p className="text-sm text-muted-foreground">
 Número da Fatura
 </p>
 <p className="font-medium text-foreground">
 {invoice.invoiceNumber}
 </p>
 </div>
 <div>
 <p className="text-sm text-muted-foreground">
 Data de Emissão
 </p>
 <p className="font-medium text-foreground">
 {formatDateToBR(invoice.issueDate)}
 </p>
 </div>
 <div>
 <p className="text-sm text-muted-foreground">
 Data de Vencimento
 </p>
 <p className="font-medium text-foreground">
 {formatDateToBR(invoice.dueDate)}
 </p>
 </div>
 <div>
 <p className="text-sm text-muted-foreground">
 Valor Total
 </p>
 <p className="font-medium text-lg text-foreground">
 {formatCurrency(totalAmount)}
 </p>
 </div>
 <div>
 <p className="text-sm text-muted-foreground">
 Valor Pago
 </p>
 <p className="font-medium text-lg text-green-600 dark:text-green-400">
 {formatCurrency(paidAmount)}
 </p>
 </div>
 {remaining > 0 && (
 <div>
 <p className="text-sm text-muted-foreground">
 Valor Restante
 </p>
 <p className="font-medium text-lg text-amber-600 dark:text-amber-400">
 {formatCurrency(remaining)}
 </p>
 </div>
 )}
 </CardContent>
 </Card>

 {/* Informações do Cliente */}
 {invoice.sale?.customer && (
 <Card>
 <CardHeader>
 <CardTitle className="flex items-center gap-2">
 <User className="h-5 w-5"/>
 Cliente
 </CardTitle>
 </CardHeader>
 <CardContent className="space-y-3">
 <div>
 <p className="text-sm text-muted-foreground">
 Nome
 </p>
 <p className="font-medium text-foreground">
 {invoice.sale.customer.name}
 </p>
 </div>
 {invoice.sale.customer.document && (
 <div>
 <p className="text-sm text-muted-foreground">
 Documento
 </p>
 <p className="font-medium text-foreground">
 {invoice.sale.customer.document}
 </p>
 </div>
 )}
 {invoice.sale.customer.email && (
 <div>
 <p className="text-sm text-muted-foreground">
 E-mail
 </p>
 <p className="font-medium text-foreground">
 {invoice.sale.customer.email}
 </p>
 </div>
 )}
 {invoice.sale.customer.phone && (
 <div>
 <p className="text-sm text-muted-foreground">
 Telefone
 </p>
 <p className="font-medium text-foreground">
 {invoice.sale.customer.phone}
 </p>
 </div>
 )}
 </CardContent>
 </Card>
 )}

 {/* Informações do Veículo */}
 {invoice.sale?.vehicle && (
 <Card>
 <CardHeader>
 <CardTitle className="flex items-center gap-2">
 <Car className="h-5 w-5"/>
 Veículo
 </CardTitle>
 </CardHeader>
 <CardContent className="space-y-3">
 <div>
 <p className="text-sm text-muted-foreground">
 Marca
 </p>
 <p className="font-medium text-foreground">
 {(invoice.sale.vehicle as any)?.brand?.name ||"N/A"}
 </p>
 </div>
 <div>
 <p className="text-sm text-muted-foreground">
 Modelo
 </p>
 <p className="font-medium text-foreground">
 {(invoice.sale.vehicle as any)?.model?.name ||"N/A"}
 </p>
 </div>
 {(invoice.sale.vehicle as any)?.modelYear && (
 <div>
 <p className="text-sm text-muted-foreground">
 Ano
 </p>
 <p className="font-medium text-foreground">
 {(invoice.sale.vehicle as any).modelYear}
 </p>
 </div>
 )}
 {(invoice.sale.vehicle as any)?.plate && (
 <div>
 <p className="text-sm text-muted-foreground">
 Placa
 </p>
 <p className="font-medium text-foreground">
 {(invoice.sale.vehicle as any).plate}
 </p>
 </div>
 )}
 </CardContent>
 </Card>
 )}
 </div>
 </TabsContent>

 {/* Payments Tab */}
 <TabsContent value="payments"className="space-y-4">
 <Card>
 <CardHeader>
 <CardTitle className="flex items-center gap-2">
 <CreditCard className="h-5 w-5"/>
 Pagamentos
 </CardTitle>
 </CardHeader>
 <CardContent>
 {!invoice.payments || invoice.payments.length === 0 ? (
 <div className="text-center py-8">
 <CreditCard className="h-12 w-12 text-muted-foreground mx-auto mb-4"/>
 <p className="text-muted-foreground">
 Nenhum pagamento registrado para esta fatura
 </p>
 </div>
 ) : (
 <Table>
 <TableHeader>
 <TableRow>
 <TableHead>Data</TableHead>
 <TableHead>Valor</TableHead>
 <TableHead>Método</TableHead>
 <TableHead>Registrado por</TableHead>
 <TableHead>Observações</TableHead>
 </TableRow>
 </TableHeader>
 <TableBody>
 {invoice.payments.map((payment: Payment) => (
 <TableRow key={payment.id}>
 <TableCell>
 {formatDateToBR(payment.paymentDate)}
 </TableCell>
 <TableCell className="font-medium">
 {formatCurrency(
 parseFloat(payment.amount.toString())
 )}
 </TableCell>
 <TableCell>{payment.paymentMethod ||"N/A"}</TableCell>
 <TableCell>{payment.creator?.name ||"N/A"}</TableCell>
 <TableCell>{payment.notes ||"-"}</TableCell>
 </TableRow>
 ))}
 </TableBody>
 </Table>
 )}
 </CardContent>
 </Card>
 </TabsContent>

 {/* Commissions Tab */}
 <TabsContent value="commissions"className="space-y-4">
 {invoice.sale && (
 <CommissionActiveRulesTable
 rules={sortedActiveCommissionRules}
 sale={invoice.sale}
 evalContext={invoiceSaleCommissionEvalContext as any}
 />
 )}
 {visibleInvoiceCommissions.length > 0 ? (
 <Card>
 <CardHeader>
 <div className="flex items-center justify-between">
 <CardTitle className="flex items-center gap-2">
 <DollarSign className="h-5 w-5"/>
 Comissões ({visibleInvoiceCommissions.length})
 </CardTitle>
 <ModernButton
 onClick={() => setAddCommissionDialogOpen(true)}
 size="sm"
 >
 <Plus className="h-4 w-4 mr-2"/>
 Adicionar Comissão
 </ModernButton>
 </div>
 </CardHeader>
 <CardContent>
 <Table>
 <TableHeader>
 <TableRow>
 <TableHead>Vendedor</TableHead>
 <TableHead>Regra</TableHead>
 <TableHead>Valor Base</TableHead>
 <TableHead>Percentual</TableHead>
 <TableHead className="text-right">{t("financialSales.commissions.fixedValueColumn")}</TableHead>
 <TableHead>Valor Calculado</TableHead>
 <TableHead className="text-right">{t("financialSales.commissions.bonusColumn")}</TableHead>
 <TableHead className="text-right">{t("financialSales.commissions.totalWithBonusesColumn")}</TableHead>
 <TableHead>Status</TableHead>
 <TableHead>Data Pagamento</TableHead>
 </TableRow>
 </TableHeader>
 <TableBody>
 {visibleInvoiceCommissions.map((commission: Commission) => {
 const display = resolveCommissionDisplay(commission, invoice.sale ?? undefined);
 const ruleForRow =
 commission.rule ??
 sortedActiveCommissionRules.find((rule) => rule.id === commission.ruleId) ??
 undefined;
 const getCommissionStatusColor = (
 status: Commission["status"]
 ) => {
 switch (status) {
 case"paid":
 return"bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
 case"pending":
 return"bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
 default:
 return"bg-surface text-foreground dark:text-muted-foreground";
 }
 };
 const getCommissionStatusLabel = (
 status: Commission["status"]
 ) => {
 const labels: Record<Commission["status"], string> = {
 pending:"Pendente",
 paid:"Paga",
 cancelled:"Cancelada",
 };
 return labels[status] || status;
 };
 const bonusSum = sumCommissionBonuses(commission);
 const totalWithBonuses = display.calculatedValue + bonusSum;
 return (
 <TableRow key={commission.id}>
 <TableCell>
 {commission.salesperson?.name ||"N/A"}
 </TableCell>
 <TableCell>
 {commission.rule?.name ||"N/A"}
 </TableCell>
 <TableCell>
 {formatCurrency(display.baseValue)}
 </TableCell>
 <TableCell>
 {display.percentage.toFixed(2)}%
 </TableCell>
 <TableCell className="text-right text-muted-foreground">
{ruleForRow?.commissionType === "mixed" ? (
<div className="leading-tight">
{t("financialSales.commissions.mixedBreakdown", {
fixed: formatCurrency(commissionRuleFixedAmount(ruleForRow)),
percent: `${display.percentage.toFixed(2)}%`,
totalPercent:
display.baseValue > 0
? `${((display.calculatedValue / display.baseValue) * 100).toFixed(2)}%`
: "—",
})}
</div>
) : commissionRuleHasFixedPortion(ruleForRow) ? (
formatCurrency(commissionRuleFixedAmount(ruleForRow))
) : (
"—"
)}
 </TableCell>
 <TableCell className="font-medium">
 {formatCurrency(display.calculatedValue)}
 </TableCell>
 <TableCell className="text-right text-muted-foreground">
 {bonusSum > 0 ? formatCurrency(bonusSum) : "—"}
 </TableCell>
 <TableCell className="text-right font-medium">
 {formatCurrency(totalWithBonuses)}
 </TableCell>
 <TableCell>
 <Badge
 className={getCommissionStatusColor(
 commission.status
 )}
 >
 {getCommissionStatusLabel(commission.status)}
 </Badge>
 </TableCell>
 <TableCell>
 {commission.paidAt
 ? formatDateToBR(commission.paidAt)
 :"-"}
 </TableCell>
 </TableRow>
 );
 })}
 </TableBody>
 </Table>
 </CardContent>
 </Card>
 ) : (
 <Card>
 <CardContent className="py-8 text-center">
 <DollarSign className="h-12 w-12 text-muted-foreground mx-auto mb-4"/>
 <p className="text-muted-foreground mb-4">
 Nenhuma comissão registrada para esta venda
 </p>
 <ModernButton
 onClick={() => setAddCommissionDialogOpen(true)}
 variant="outline"
 >
 <Plus className="h-4 w-4 mr-2"/>
 Adicionar Comissão
 </ModernButton>
 </CardContent>
 </Card>
 )}

 {/* Add Commission - Inline */}
 {addCommissionDialogOpen && (
 <Card className="border-accent/30">
 <CardHeader className="pb-3">
 <div className="flex items-center justify-between">
 <CardTitle className="text-base">Adicionar Comissao</CardTitle>
 <ModernButton variant="outline" size="sm" className="px-2" onClick={() => { setAddCommissionDialogOpen(false); setSelectedRuleId(""); }}>
 x
 </ModernButton>
 </div>
 </CardHeader>
 <CardContent>
 <div className="space-y-4">
 <div>
 <Label>Regra de Comissao *</Label>
 <Select value={selectedRuleId} onValueChange={setSelectedRuleId}>
 <SelectTrigger><SelectValue placeholder="Selecione uma regra de comissao"/></SelectTrigger>
 <SelectContent>
 {availableRules.length === 0 ? (
 <SelectItem value="none" disabled>
 {t("financialSales.commissionRuleEvaluation.noRulesInSelect")}
 </SelectItem>
 ) : (
 availableRules.map((rule) => (
 <SelectItem key={rule.id} value={rule.id}>{rule.name}{rule.description && ` - ${rule.description}`}</SelectItem>
 ))
 )}
 </SelectContent>
 </Select>
 {availableRules.length === 0 && <p className="text-sm text-muted-foreground mt-2">Nenhuma regra de comissao aplicavel encontrada para esta venda.</p>}
 </div>
 <div className="flex justify-end gap-2">
 <ModernButton variant="outline"onClick={() => { setAddCommissionDialogOpen(false); setSelectedRuleId(""); }} disabled={isCreatingCommission}>Cancelar</ModernButton>
 <ModernButton onClick={handleAddCommission} disabled={!selectedRuleId || isCreatingCommission || availableRules.length === 0}>
 {isCreatingCommission ?"Adicionando...":"Adicionar"}
 </ModernButton>
 </div>
 </div>
 </CardContent>
 </Card>
 )}
 </TabsContent>

 {/* Sale Tab */}
 <TabsContent value="sale"className="space-y-4">
 {invoice.sale ? (
 <Card>
 <CardHeader>
 <CardTitle className="flex items-center gap-2">
 <DollarSign className="h-5 w-5"/>
 Venda Relacionada
 </CardTitle>
 </CardHeader>
 <CardContent className="space-y-4">
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div>
 <p className="text-sm text-muted-foreground">
 ID da Venda
 </p>
 <p className="font-medium text-foreground">
 {invoice.sale.id.substring(0, 8)}...
 </p>
 </div>
 <div>
 <p className="text-sm text-muted-foreground">
 Data da Venda
 </p>
 <p className="font-medium text-foreground">
 {formatDateToBR(invoice.sale.saleDate)}
 </p>
 </div>
 <div>
 <p className="text-sm text-muted-foreground">
 Valor Total
 </p>
 <p className="font-medium text-foreground">
 {formatCurrency(
 parseFloat(invoice.sale.totalValue.toString())
 )}
 </p>
 </div>
 <div>
 <p className="text-sm text-muted-foreground">
 Valor Final
 </p>
 <p className="font-medium text-foreground">
 {formatCurrency(
 parseFloat(invoice.sale.finalValue.toString())
 )}
 </p>
 </div>
 {invoice.sale.salesperson && (
 <div>
 <p className="text-sm text-muted-foreground">
 Vendedor
 </p>
 <p className="font-medium text-foreground">
 {invoice.sale.salesperson.name}
 </p>
 </div>
 )}
 </div>

 {invoice.sale.charges && invoice.sale.charges.length > 0 && (
 <div className="mt-4">
 <h4 className="font-medium text-foreground mb-2">
 Encargos da Venda
 </h4>
 <div className="space-y-2">
 {invoice.sale.charges.map((charge) => (
 <div
 key={charge.id}
 className="flex justify-between items-center p-2 bg-surface rounded"
 >
 <span className="text-sm text-foreground">
 {charge.charge?.name ||"Encargo"}
 </span>
 <span className="font-medium text-foreground">
 {formatCurrency(
 parseFloat(charge.value.toString())
 )}
 </span>
 </div>
 ))}
 </div>
 </div>
 )}
 </CardContent>
 </Card>
 ) : (
 <Card>
 <CardContent className="py-8 text-center">
 <p className="text-muted-foreground">
 Nenhuma venda relacionada encontrada
 </p>
 </CardContent>
 </Card>
 )}
 </TabsContent>
 </Tabs>

 {toast && (
 <Toast
 message={toast.message}
 type={toast.type}
 onClose={() => setToast(null)}
 duration={3000}
 />
 )}
 </div>
 );
};
